import { useCallback, useEffect, useState } from "react";
import { listSuppressions } from "./api";
import { SuppressedEmail } from "./types";

interface UseSuppressions {
  rows: SuppressedEmail[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Drop a row locally after a successful un-suppress (avoids a full refetch). */
  removeLocal: (email: string) => void;
}

/**
 * Load the suppression list. `enabled` gates the fetch on staff auth so a
 * non-staff session never fires the (403) request.
 */
export function useSuppressions(enabled: boolean): UseSuppressions {
  const [rows, setRows] = useState<SuppressedEmail[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await listSuppressions());
    } catch (e: any) {
      setError(e?.message ?? "Failed to load the suppression list.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refetch();
  }, [enabled, refetch]);

  const removeLocal = useCallback((email: string) => {
    setRows((current) => current.filter((r) => r.email !== email));
  }, []);

  return { rows, loading, error, refetch, removeLocal };
}
